import React, { useEffect, useState } from "react";
import { Recycle, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import ClientPicker from "@/components/ClientPicker";

const SOURCE_LABELS = {
    agent_cache: "Cached AI outputs",
    datalake: "Datalake reads",
    common_knowledge: "Common knowledge",
    rag: "RAG retrieval",
};

export default function ReuseRevenueSection() {
    const [days, setDays] = useState(30);
    const [summary, setSummary] = useState(null);
    const [charges, setCharges] = useState([]);
    const [rateInfo, setRateInfo] = useState(null);
    const [editingRate, setEditingRate] = useState(false);
    const [rateInput, setRateInput] = useState("");
    const [savingRate, setSavingRate] = useState(false);
    const [loading, setLoading] = useState(true);
    const [tenantFilter, setTenantFilter] = useState("");

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            params.set("days", String(days));
            params.set("top_n", "20");
            if (tenantFilter.trim()) params.set("client_id", tenantFilter.trim());
            const [s, ch, rt] = await Promise.all([
                api.get(`/admin/billing/reuse/summary?${params.toString()}`).then((r) => r.data),
                api.get(`/admin/billing/reuse/charges?limit=200${tenantFilter ? `&client_id=${encodeURIComponent(tenantFilter.trim())}` : ""}`).then((r) => r.data),
                api.get(`/admin/billing/reuse/rate`).then((r) => r.data).catch(() => null),
            ]);
            setSummary(s);
            setCharges(ch?.items || []);
            setRateInfo(rt);
            setRateInput(rt?.rate_usd_per_doc != null ? String(rt.rate_usd_per_doc) : "");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load reuse summary");
        } finally {
            setLoading(false);
        }
    }, [days, tenantFilter]);

    useEffect(() => { load(); }, [load]);

    const saveRate = async () => {
        const v = Number(rateInput);
        if (!Number.isFinite(v) || v < 0) {
            toast.error("Rate must be a non-negative number");
            return;
        }
        setSavingRate(true);
        try {
            await api.patch(`/admin/settings`, { reuse_charge_usd_per_doc: v });
            toast.success(`Global reuse rate set to $${v.toFixed(4)}/doc`);
            setEditingRate(false);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to update rate");
        } finally {
            setSavingRate(false);
        }
    };

    return (
        <section id="reuse" className="mt-10 scroll-mt-20" data-testid="reuse-revenue-section">
            <div className="flex items-end justify-between gap-4 mb-4 flex-wrap">
                <div>
                    <div className="label-xs text-[#7C3AED] flex items-center gap-2">
                        <Recycle className="w-3 h-3" /> Reuse Revenue
                    </div>
                    <h2 className="font-display text-2xl tracking-tight mt-2">Revenue from cached &amp; reused content</h2>
                    <p className="text-sm text-white/50 mt-1.5 max-w-2xl">
                        Each tenant pays a per-document fee whenever the platform serves a cached AI output,
                        a datalake item, a common-knowledge snippet, or a RAG hit. The QA superdomain
                        (<span className="font-mono text-white/70">sitecentric</span>) is exempt.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <ClientPicker
                        value={tenantFilter}
                        onChange={setTenantFilter}
                        compact
                        width="w-56"
                        anyOptionLabel="all tenants"
                        testId="reuse-tenant-filter"
                    />
                    <div className="flex items-center gap-1 p-1 bg-[#0a0a0a] border border-white/10 rounded-md">
                        {[7, 30, 90].map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                data-testid={`reuse-days-${d}`}
                                className={`px-3 py-1.5 text-xs font-mono ${days === d ? "bg-[#7C3AED] text-white" : "text-white/60 hover:text-white"}`}
                            >
                                {d}d
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stat row */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                <ReuseStat label="Revenue" value={`$${(summary?.total_usd || 0).toFixed(4)}`} hint="incl. all sources" testId="reuse-stat-revenue" />
                <ReuseStat label="Documents reused" value={(summary?.total_docs || 0).toLocaleString()} hint={`${(summary?.total_events || 0).toLocaleString()} events`} testId="reuse-stat-docs" />
                <ReuseStat label="Tenants charged" value={(summary?.by_tenant || []).length} hint="non-exempt only" testId="reuse-stat-tenants" />
                <RateBox
                    rateInfo={rateInfo}
                    editing={editingRate}
                    onEdit={() => setEditingRate(true)}
                    onCancel={() => { setEditingRate(false); setRateInput(rateInfo?.rate_usd_per_doc != null ? String(rateInfo.rate_usd_per_doc) : ""); }}
                    onSave={saveRate}
                    saving={savingRate}
                    rateInput={rateInput}
                    setRateInput={setRateInput}
                />
            </div>

            {/* By source breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-5">
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="reuse-by-source">
                    <div className="label-xs mb-3">Revenue by source</div>
                    {loading ? <div className="text-xs text-white/40">Loading…</div> : (
                        <div className="space-y-3">
                            {Object.entries(summary?.by_source || {}).length === 0 ? (
                                <div className="text-xs text-white/40">No reuse events in this window yet.</div>
                            ) : Object.entries(summary?.by_source || {}).map(([src, s]) => {
                                const total = summary?.total_usd || 0;
                                const pct = total ? Math.round(((s?.total_usd || 0) / total) * 100) : 0;
                                return (
                                    <div key={src} data-testid={`reuse-source-${src}`}>
                                        <div className="flex items-center justify-between text-xs font-mono">
                                            <span className="text-white/70">{SOURCE_LABELS[src] || src}</span>
                                            <span className="text-white">${(s?.total_usd || 0).toFixed(4)} · {(s?.total_docs || 0).toLocaleString()} docs</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-white/5 rounded-sm mt-1.5 overflow-hidden">
                                            <div className="h-full bg-[#7C3AED]" style={{ width: `${pct}%` }} />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Top tenants */}
                <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5" data-testid="reuse-top-tenants">
                    <div className="label-xs mb-3">Top tenants by reuse revenue</div>
                    {loading ? <div className="text-xs text-white/40">Loading…</div> : (
                        <div className="space-y-2">
                            {(summary?.by_tenant || []).length === 0 ? (
                                <div className="text-xs text-white/40">No paying tenants in this window yet.</div>
                            ) : (summary?.by_tenant || []).map((t) => (
                                <div key={t.client_id || "unknown"} className="flex items-center justify-between text-xs font-mono py-1.5 border-b border-white/5 last:border-b-0">
                                    <button
                                        onClick={() => setTenantFilter(t.client_id || "")}
                                        className="text-white/80 hover:text-[#7C3AED] truncate text-left"
                                        data-testid={`reuse-tenant-row-${t.client_id || "unknown"}`}
                                    >
                                        {t.client_id || <span className="text-white/40">(no client_id)</span>}
                                    </button>
                                    <span className="text-white whitespace-nowrap">${(t.total_usd || 0).toFixed(4)} · {(t.total_docs || 0).toLocaleString()} docs</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Recent charges */}
            <div className="bg-[#0a0a0a] border border-white/10 rounded-md overflow-hidden" data-testid="reuse-charges-table">
                <div className="px-5 py-3 border-b border-white/10 flex items-center justify-between">
                    <span className="label-xs">Recent charge events</span>
                    <span className="text-[11px] font-mono text-white/40">{charges.length} rows</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs font-mono">
                        <thead className="bg-white/[0.03] text-white/50">
                            <tr>
                                <th className="text-left py-2 px-4">When</th>
                                <th className="text-left py-2 px-4">Tenant</th>
                                <th className="text-left py-2 px-4">Source</th>
                                <th className="text-right py-2 px-4">Docs</th>
                                <th className="text-right py-2 px-4">Rate</th>
                                <th className="text-right py-2 px-4">Total</th>
                                <th className="text-left py-2 px-4">Actor</th>
                            </tr>
                        </thead>
                        <tbody>
                            {charges.length === 0 ? (
                                <tr><td colSpan={7} className="py-6 px-4 text-center text-white/40">
                                    {loading ? "Loading…" : "No reuse charges yet."}
                                </td></tr>
                            ) : charges.map((c) => (
                                <tr key={c.id} className="border-t border-white/5">
                                    <td className="py-2 px-4 text-white/60">{(c.created_at || "").replace("T", " ").slice(0, 19)}</td>
                                    <td className="py-2 px-4 text-white/80">{c.client_id || "-"}</td>
                                    <td className="py-2 px-4 text-[#c7a6ff]">{SOURCE_LABELS[c.source] || c.source}</td>
                                    <td className="py-2 px-4 text-right text-white">{(c.docs_count || 0).toLocaleString()}</td>
                                    <td className="py-2 px-4 text-right text-white/60">${(c.rate_usd || 0).toFixed(4)}</td>
                                    <td className="py-2 px-4 text-right text-white">${(c.total_usd || 0).toFixed(4)}</td>
                                    <td className="py-2 px-4 text-white/50 truncate max-w-[200px]">{c.actor_email || c.actor_kind || "-"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}

function ReuseStat({ label, value, hint, testId }) {
    return (
        <div data-testid={testId} className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 min-h-[110px] flex flex-col justify-between">
            <span className="label-xs">{label}</span>
            <div>
                <div className="font-display text-2xl tracking-tight">{value}</div>
                {hint ? <div className="text-[11px] font-mono text-white/40 mt-1">{hint}</div> : null}
            </div>
        </div>
    );
}

function RateBox({ rateInfo, editing, onEdit, onCancel, onSave, saving, rateInput, setRateInput }) {
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-5 min-h-[110px] flex flex-col justify-between" data-testid="reuse-rate-box">
            <div className="flex items-center justify-between">
                <span className="label-xs">Effective rate</span>
                {!editing ? (
                    <button
                        onClick={onEdit}
                        data-testid="reuse-rate-edit"
                        className="text-[10px] font-mono text-[#7C3AED] hover:text-[#a17bff]"
                    >
                        edit global
                    </button>
                ) : null}
            </div>
            {!editing ? (
                <div>
                    <div className="font-display text-2xl tracking-tight">
                        ${Number(rateInfo?.rate_usd_per_doc || 0).toFixed(4)}
                        <span className="text-xs font-mono text-white/40 ml-1">/doc</span>
                    </div>
                    <div className="text-[10px] font-mono text-white/40 mt-1 flex items-center gap-1">
                        scope: <span className="text-white/70">{rateInfo?.scope || "global"}</span>
                        {rateInfo?.scope === "exempt" ? <AlertTriangle className="w-3 h-3 text-amber-400" /> : null}
                    </div>
                </div>
            ) : (
                <div className="flex items-center gap-2 mt-2">
                    <input
                        type="number"
                        step="0.001"
                        min="0"
                        value={rateInput}
                        onChange={(e) => setRateInput(e.target.value)}
                        data-testid="reuse-rate-input"
                        className="px-2 py-1.5 bg-black border border-[#7C3AED]/40 rounded-md text-sm font-mono w-24"
                    />
                    <button
                        onClick={onSave}
                        disabled={saving}
                        data-testid="reuse-rate-save"
                        className="px-3 py-1.5 bg-[#7C3AED] hover:bg-[#6928d6] disabled:opacity-50 text-white text-xs font-mono rounded-md"
                    >
                        {saving ? "…" : "Save"}
                    </button>
                    <button
                        onClick={onCancel}
                        data-testid="reuse-rate-cancel"
                        className="px-3 py-1.5 border border-white/10 text-white/60 hover:text-white text-xs font-mono rounded-md"
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
}
